import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const framingCache = new Map();
const MAX_CACHED_IMAGES = 256;
const SAMPLE_SIZE = 320;

// Only remove demonstrably transparent margins. Opaque white pixels may be
// packaging or pale produce and must remain part of the catalog photograph.
export const findTransparentContentBounds = ({ data, width, height }) => {
  if (!width || !height || data.length !== width * height * 4) return null;
  const alpha = (x, y) => data[(y * width + x) * 4 + 3];
  for (let x = 0; x < width; x += 1) {
    if (alpha(x, 0) || alpha(x, height - 1)) return null;
  }
  for (let y = 0; y < height; y += 1) {
    if (alpha(0, y) || alpha(width - 1, y)) return null;
  }

  let left = width;
  let top = height;
  let right = -1;
  let bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      // Include even very faint shadows, anti-aliased stems, and disconnected
      // pieces. Downsampling plus a safety margin keeps their full silhouette.
      if (alpha(x, y) > 0) {
        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x);
        bottom = Math.max(bottom, y);
      }
    }
  }
  if (right < left || bottom < top) return null;
  const padX = Math.max(3, Math.ceil(width * 0.035));
  const padY = Math.max(3, Math.ceil(height * 0.035));
  left = Math.max(0, left - padX);
  top = Math.max(0, top - padY);
  right = Math.min(width, right + 1 + padX);
  bottom = Math.min(height, bottom + 1 + padY);
  if ((right - left) / width > 0.92 && (bottom - top) / height > 0.92) return null;
  return { x: left / width, y: top / height, width: (right - left) / width, height: (bottom - top) / height };
};

export const fitDisplayImage = (sourceWidth, sourceHeight, bounds, slotWidth, slotHeight) => {
  if (!bounds || sourceWidth <= 0 || sourceHeight <= 0 || slotWidth <= 0 || slotHeight <= 0) return null;
  const cropWidth = bounds.width * sourceWidth;
  const cropHeight = bounds.height * sourceHeight;
  if (cropWidth <= 0 || cropHeight <= 0) return null;
  const scale = Math.min(slotWidth / cropWidth, slotHeight / cropHeight);
  return {
    width: sourceWidth * scale,
    height: sourceHeight * scale,
    left: (slotWidth - cropWidth * scale) / 2 - bounds.x * sourceWidth * scale,
    top: (slotHeight - cropHeight * scale) / 2 - bounds.y * sourceHeight * scale,
  };
};

const inspectImage = (imageUrl) => {
  if (framingCache.has(imageUrl)) return framingCache.get(imageUrl);
  const inspection = new Promise((resolve) => {
    const probe = new Image();
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let settled = false;
    let timeout;
    let objectUrl;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      probe.onload = null;
      probe.onerror = null;
      controller?.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      resolve(result);
    };
    probe.crossOrigin = 'anonymous';
    probe.onload = () => {
      try {
        if (!probe.naturalWidth || !probe.naturalHeight) return finish(null);
        const ratio = Math.min(1, SAMPLE_SIZE / Math.max(probe.naturalWidth, probe.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(probe.naturalWidth * ratio));
        canvas.height = Math.max(1, Math.round(probe.naturalHeight * ratio));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return finish(null);
        context.drawImage(probe, 0, 0, canvas.width, canvas.height);
        const bounds = findTransparentContentBounds(context.getImageData(0, 0, canvas.width, canvas.height));
        finish(bounds ? { bounds, sourceWidth: probe.naturalWidth, sourceHeight: probe.naturalHeight } : null);
      } catch {
        // A server may disallow canvas access. The visible original <img>
        // uses its normal request mode and remains available in that case.
        finish(null);
      }
      return undefined;
    };
    probe.onerror = () => finish(null);
    timeout = window.setTimeout(() => finish(null), 8000);
    if (typeof fetch === 'function' && typeof URL.createObjectURL === 'function') {
      // Some image hosts omit Vary: Origin on ordinary image responses. A
      // normal preloader can therefore poison a later CORS cache lookup.
      // Reload only this public image, without credentials or URL changes.
      fetch(imageUrl, { mode: 'cors', credentials: 'omit', cache: 'reload', signal: controller?.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Image inspection unavailable');
          return response.blob();
        })
        .then((blob) => {
          if (settled) return;
          objectUrl = URL.createObjectURL(blob);
          probe.src = objectUrl;
        })
        .catch(() => finish(null));
    } else {
      probe.src = imageUrl;
    }
  });
  framingCache.set(imageUrl, inspection);
  if (framingCache.size > MAX_CACHED_IMAGES) framingCache.delete(framingCache.keys().next().value);
  return inspection;
};

// Retain only a small decoded look-ahead window; framing metadata is tiny and
// can cover a full 200-item playlist without refetching every loop.
const decodedImages = new Map();
export const prepareDisplayImage = (imageUrl) => {
  if (!imageUrl) return Promise.resolve();
  inspectImage(imageUrl);
  if (decodedImages.has(imageUrl)) return decodedImages.get(imageUrl).ready;
  const image = new Image();
  const ready = new Promise((resolve) => {
    let timer;
    const finish = () => { window.clearTimeout(timer); image.onload = null; image.onerror = null; resolve(); };
    image.onload = () => { if (image.decode) image.decode().catch(() => {}).then(finish); else finish(); };
    image.onerror = () => { decodedImages.delete(imageUrl); finish(); };
    timer = window.setTimeout(() => { decodedImages.delete(imageUrl); finish(); }, 8000);
    image.src = imageUrl;
  });
  decodedImages.set(imageUrl, { image, ready });
  if (decodedImages.size > 12) decodedImages.delete(decodedImages.keys().next().value);
  return ready;
};

const DisplayProductImage = ({ imageUrl, alt = '', onError, className }) => {
  const slot = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [framing, setFraming] = useState(null);

  useEffect(() => {
    let active = true;
    if (imageUrl) inspectImage(imageUrl).then((result) => {
      if (active) setFraming({ imageUrl, result });
    });
    return () => { active = false; };
  }, [imageUrl]);

  useLayoutEffect(() => {
    if (!slot.current) return undefined;
    const measure = () => {
      if (!slot.current) return;
      const { width, height } = slot.current.getBoundingClientRect();
      setSize((previous) => previous.width === width && previous.height === height ? previous : { width, height });
    };
    measure();
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(measure);
      observer.observe(slot.current);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [imageUrl]);

  const current = framing?.imageUrl === imageUrl ? framing.result : null;
  const geometry = current && fitDisplayImage(current.sourceWidth, current.sourceHeight, current.bounds, size.width, size.height);
  return imageUrl ? (
    <span ref={slot} className="branch-display-image-frame" data-framing={geometry ? 'transparent-trim' : 'original'} style={{ position: 'relative', display: 'block', width: '100%', height: '100%', overflow: 'hidden' }}>
      <img
        src={imageUrl}
        alt={alt}
        className={className}
        onError={onError}
        draggable={false}
        decoding="async"
        style={geometry ? { position: 'absolute', display: 'block', ...geometry, objectFit: 'fill', maxWidth: 'none', maxHeight: 'none' } : { display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </span>
  ) : null;
};

export default DisplayProductImage;
